import {
  createListCollection,
  HStack,
  Icon,
  IconButton,
  SelectValueChangeDetails,
  StackProps,
  Text,
} from "@chakra-ui/react";
import {typedEntries, typedMemo} from "../../../../utils/typescript";
import React, {ReactNode, RefObject, SetStateAction, useCallback, useMemo} from "react";
import {SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText} from "../../../ui/select";
import {Tooltip} from "../../../ui/tooltip";
import {BarsArrowDownIcon, BarsArrowUpIcon, QueueListIcon} from "@heroicons/react/16/solid";
import {
  Column,
  ExtractTableContext,
  GroupState,
  GroupStateEntry,
  isColumnGroupable,
  isColumnHidden,
  TableConfig,
} from "../../../../hooks/table";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";

const NONE_VALUE = "__NONE__";

function isSelectableGroup<Col extends Column>(column: Col, groupState: GroupState, depth: number): boolean {
  return (
    isColumnGroupable(column) &&
    (column.group.parentGroupColumnId == null ||
      (depth > 0 && column.group.parentGroupColumnId === groupState[depth - 1].columnId))
  );
}

export const GroupingSelectors = typedMemo(
  <C extends TableConfig>({
    config,
    context,
    groupState,
    onGroupStateChange,
    portalRef,
  }: {
    config: C;
    context: ExtractTableContext<C>;
    groupState: GroupState<C>;
    onGroupStateChange: (action: SetStateAction<GroupState<C>>) => void;
    portalRef: RefObject<HTMLDivElement>;
  }) => {
    const maxGroups = config.enableSubGrouping == true ? 2 : 1;
    const entries: (GroupStateEntry<C> | null)[] = [...groupState, null];

    return entries.slice(0, maxGroups).map((entry, depth) => (
      <React.Fragment key={depth}>
        <HStack color="fg.muted">
          <Icon boxSize="1em">
            <QueueListIcon />
          </Icon>
          <Text fontWeight="medium">
            {depth === 0 ? (
              <T keyName="c.Grouping" />
            ) : depth === 1 ? (
              <T keyName="c.Sub-grouping" />
            ) : (
              <T keyName="saved_views.sub_grouping_level" params={{level: depth}} />
            )}
          </Text>
        </HStack>
        <GroupingSelector
          config={config}
          context={context}
          entry={entry}
          groupState={groupState}
          onGroupStateChange={onGroupStateChange}
          depth={depth}
          portalRef={portalRef}
          containerProps={{flex: "1 1 auto"}}
        />
      </React.Fragment>
    ));
  },
);

const GroupingSelector = typedMemo(
  <C extends TableConfig>({
    config,
    context,
    entry,
    groupState,
    onGroupStateChange,
    depth,
    portalRef,
    containerProps,
  }: {
    config: C;
    context: ExtractTableContext<C>;
    entry: GroupStateEntry<C> | null;
    groupState: GroupState<C>;
    onGroupStateChange: (action: SetStateAction<GroupState<C>>) => void;
    depth: number;
    portalRef: RefObject<HTMLDivElement>;
    containerProps?: StackProps;
  }) => {
    const t = useTranslate();
    const groupableColumns = useMemo(
      () => typedEntries(config.columns).filter(([_, column]) => isSelectableGroup(column, groupState, depth)),
      [config.columns, depth, groupState],
    );

    const collection = useMemo(
      () =>
        createListCollection({
          items: [
            {label: t("c.No_grouping"), labelNode: undefined as ReactNode, value: NONE_VALUE, hidden: false},
            ...groupableColumns.map(([columnId, column]) => ({
              label: column.overrideGroupAndSortLabel ?? column.label,
              labelNode: column.label,
              value: columnId,
              hidden: isColumnHidden(column, context),
            })),
          ],
        }),
      [context, groupableColumns, t],
    );

    const onSelectValueChange = useCallback(
      (d: SelectValueChangeDetails) => {
        const value = d.value[0];

        onGroupStateChange(prev => {
          // If we set the grouping to none, we should remove this entry and all
          // entries afterwards.
          if (value === NONE_VALUE) {
            return prev.slice(0, depth);
          }

          const newEntry: GroupStateEntry<C> = {columnId: value, direction: "ascending"};

          // If the immediate child of this group does not have this group as a
          // parent, remove all child groups.
          if (prev.length > depth + 1) {
            const childColumnId = prev[depth + 1].columnId;
            const childConfig = config.columns[childColumnId].group!;

            if (childConfig.parentGroupColumnId != null && childConfig.parentGroupColumnId !== value) {
              prev = prev.slice(0, depth);
            }
          }

          if (depth >= prev.length) {
            // Create a new entry if one does not exist at this depth.
            return [...prev, newEntry];
          } else {
            // Update the existing entry at this depth.
            return prev.map((entry, i) => {
              // Leave all other entries alone
              if (i !== depth) return entry;

              // Update our entry
              return {columnId: value, direction: "ascending"};
            });
          }
        });
      },
      [config.columns, depth, onGroupStateChange],
    );

    const onClickDirectionButton = useCallback(() => {
      onGroupStateChange(prev => {
        const entry = prev[depth];

        // Shouldn't happen- the button shouldn't be visible if the group state
        // is null.
        if (entry == null) return prev;

        return prev.map((entry, i) => {
          // Leave all other entries alone
          if (i !== depth) return entry;

          // Update our entry to the opposite direction
          return {...entry, direction: entry.direction === "ascending" ? "descending" : "ascending"};
        });
      });
    }, [depth, onGroupStateChange]);

    return (
      <HStack {...containerProps}>
        <SelectRoot
          collection={collection}
          size="sm"
          value={[entry?.columnId ?? NONE_VALUE]}
          onValueChange={onSelectValueChange}
        >
          <SelectTrigger>
            <SelectValueText placeholder={t("c.None")} />
          </SelectTrigger>
          <SelectContent portalRef={portalRef}>
            {collection.items
              .filter(item => !item.hidden)
              .map(item => (
                <SelectItem key={item.value} item={item}>
                  {item.labelNode ?? item.label}
                </SelectItem>
              ))}
          </SelectContent>
        </SelectRoot>
        {entry != null && (
          <Tooltip
            content={entry.direction === "ascending" ? <T keyName="c.Ascending" /> : <T keyName="c.Descending" />}
          >
            <IconButton size="sm" variant="outline" onClick={onClickDirectionButton}>
              {entry.direction === "ascending" ? <BarsArrowDownIcon /> : <BarsArrowUpIcon />}
            </IconButton>
          </Tooltip>
        )}
      </HStack>
    );
  },
);
